import { create } from 'zustand'
import { devtools, persist, createJSONStorage } from 'zustand/middleware'
import { zustandPlugin } from '@/modules/shared/openreplay'
import type { DayType } from '@/modules/drivers/tabs/registry/schedule/constants/DayType'

export type TimesheetViewMode =
  | 'bonus-reward'
  | 'group-view'
  | 'schedule'
  | 'single-view'

export type DayTypeFormType =
  | 'work'
  | 'vacation'
  | 'off-work'
  | 'allowances'
  | 'absences'
  | 'other'

export const DAY_TYPE_TABS: { type: DayTypeFormType; label: string }[] = [
  { type: 'work', label: 'Praca' },
  { type: 'vacation', label: 'Urlop' },
  { type: 'off-work', label: 'Wolne' },
  { type: 'absences', label: 'Nieobecności' },
  { type: 'allowances', label: 'Zasiłki i świadczenia' },
  { type: 'other', label: 'Inne' },
]

/* Preferences — persist across browser sessions.
 * Month selection lives in useDateStore (global date source of truth). */

type PrefsState = {
  viewMode: TimesheetViewMode | null
  overtimeCompensation: boolean
}

type PrefsActions = {
  setViewMode: (mode: TimesheetViewMode) => void
  setOvertimeCompensation: (on: boolean) => void
}

export const useTimesheetPrefsStore = create<PrefsState & PrefsActions>()(
  devtools(
    persist(
      zustandPlugin(
        (set) => ({
          viewMode: null,
          overtimeCompensation: false,
          setViewMode: (viewMode) => set({ viewMode }, false, 'setViewMode'),
          setOvertimeCompensation: (overtimeCompensation) =>
            set({ overtimeCompensation }, false, 'setOvertimeCompensation'),
        }),
        'timesheet-prefs',
      ),
      {
        name: 'timesheet.prefs.v1',
        storage: createJSONStorage(() => localStorage),
        partialize: (s) => ({
          viewMode: s.viewMode,
          overtimeCompensation: s.overtimeCompensation,
        }),
      },
    ),
    { name: 'EasyTacho', store: 'timesheet-prefs' },
  ),
)

/* Per-view UI state — not persisted, resets when the driver changes. */

type UIState = {
  selectedDays: string[]
  formTab: DayTypeFormType
  selectedDayType: DayType | null
  isOpenDayTypeModal: boolean
  isLoading: boolean
}

type UIActions = {
  setSelectedDays: (days: string[] | ((prev: string[]) => string[])) => void
  setFormTab: (tab: DayTypeFormType) => void
  setSelectedDayType: (type: DayType | null) => void
  setIsOpenDayTypeModal: (open: boolean) => void
  setIsLoading: (loading: boolean) => void
  reset: () => void
}

const uiInitial: UIState = {
  selectedDays: [],
  formTab: 'work',
  selectedDayType: null,
  isOpenDayTypeModal: false,
  isLoading: false,
}

export const useTimesheetUIStore = create<UIState & UIActions>()(
  devtools(
    zustandPlugin(
      (set) => ({
        ...uiInitial,
        setSelectedDays: (selectedDays) =>
          set(
            (s) => ({
              selectedDays:
                typeof selectedDays === 'function'
                  ? selectedDays(s.selectedDays)
                  : selectedDays,
            }),
            false,
            'setSelectedDays',
          ),
        setFormTab: (formTab) => set({ formTab }, false, 'setFormTab'),
        setSelectedDayType: (selectedDayType) =>
          set({ selectedDayType }, false, 'setSelectedDayType'),
        setIsOpenDayTypeModal: (isOpenDayTypeModal) =>
          set({ isOpenDayTypeModal }, false, 'setIsOpenDayTypeModal'),
        setIsLoading: (isLoading) => set({ isLoading }, false, 'setIsLoading'),
        reset: () => set(uiInitial, false, 'reset'),
      }),
      'timesheet-ui',
    ),
    { name: 'EasyTacho', store: 'timesheet-ui' },
  ),
)
